import type { ApiService } from '@/services/api'
import type { PDFFile, TextSelection } from '@/types'
import type { DocumentViewer, Document, SelectionCoords } from '@/interfaces/document-viewer'
import { SelectionsGallery } from '@/components/selections-gallery'
import { PDFRenderer } from '@/components/pdf-renderer'
import { SelectionManager } from '@/components/selection-manager'
import { ThumbnailGenerator } from '@/components/thumbnail-generator'
import { logger } from '@/utils/logger'

/**
 * PDF Document Viewer that uses focused, single-responsibility classes
 * This replaces the monolithic 2,960-line PDFViewer class
 */
export class PDFDocumentViewer implements DocumentViewer {
  private container: HTMLElement
  private apiService: ApiService
  private appState?: any
  
  // Focused component classes
  private renderer: PDFRenderer | null = null
  private selectionManager: SelectionManager | null = null
  private thumbnailGenerator: ThumbnailGenerator | null = null
  private selectionsGallery: SelectionsGallery | null = null
  private documentCache: Map<string, Document> = new Map()
  
  // State
  private currentFile: PDFFile | null = null
  private pdfDocument: any = null // Add PDF document reference for OCR
  private _initialSelections: TextSelection[] = []
  
  constructor(
    container: HTMLElement,
    apiService: ApiService,
    _appState?: any,
    _initialSelections: TextSelection[] = []
  ) {
    this.container = container
    this.apiService = apiService
    this.appState = _appState
    
    // Store initial selections for later loading
    this._initialSelections = _initialSelections
    
    // Make this instance available globally for debugging
    ;(window as any).pdfViewerRefactored = this
    
    this.init()
  }
  
  private init(): void {
    this.render()
    this.initializeRenderer()
    this.initializeSelectionManager()
    this.initializeThumbnailGenerator()
    this.setupComponentInteractions()
    this.setupEventListeners()
    
    logger.pdf('PDFDocumentViewer initialized with focused components')
  }
  
  /**
   * Initialize the PDF renderer after HTML is created
   */
  private initializeRenderer(): void {
    this.renderer = new PDFRenderer(this.container, this.apiService)
    
    // Set up scale change callback
    this.renderer.setOnScaleChanged((_scale) => {
      this.updateScaleFactor()
    })
    
    logger.pdf('PDF renderer initialized after HTML creation')
  }
  
  /**
   * Initialize the selection manager after HTML is created
   */
  private initializeSelectionManager(): void {
    this.selectionManager = new SelectionManager(this.container)
    
    // Set initial scale factor
    this.ensureSelectionManager().setScale(this.ensureRenderer().getScale())
    
    // Load initial selections if provided
    if (this._initialSelections.length > 0) {
      this.ensureSelectionManager().loadSelections(this._initialSelections)
    }
    
    logger.pdf('Selection manager initialized after HTML creation')
  }
  
  /**
   * Initialize the thumbnail generator after HTML is created
   */
  private initializeThumbnailGenerator(): void {
    this.thumbnailGenerator = new ThumbnailGenerator(this.container)
    logger.pdf('Thumbnail generator initialized after HTML creation')
  }
  
  /**
   * Check if renderer is available
   */
  private ensureRenderer(): PDFRenderer {
    if (!this.renderer) {
      throw new Error('PDF renderer not initialized')
    }
    return this.renderer
  }
  
  /**
   * Check if selection manager is available
   */
  private ensureSelectionManager(): SelectionManager {
    if (!this.selectionManager) {
      throw new Error('Selection manager not initialized')
    }
    return this.selectionManager
  }
  
  /**
   * Check if thumbnail generator is available
   */
  private ensureThumbnailGenerator(): ThumbnailGenerator {
    if (!this.thumbnailGenerator) {
      throw new Error('Thumbnail generator not initialized')
    }
    return this.thumbnailGenerator
  }
  
  /**
   * Setup interactions between focused components
   */
  private setupComponentInteractions(): void {
    // Selection manager callbacks
    this.ensureSelectionManager().setOnSelectionCreated(async (selection) => {
      logger.debug('Selection created, generating thumbnail:', selection.id)
      
      // 🚀 IMMEDIATE UI UPDATE: Add selection to app state immediately for instant feedback
      if (this.appState && this.appState.addSelection) {
        console.log('⚡ Adding selection to app state immediately for instant UI feedback:', selection.id)
        this.appState.addSelection(selection)
      }
      
      // Immediately update the gallery to show the new selection
      this.updateSelectionsGallery()
      
      // Notify modal about new selection for count updates
      this.notifySelectionCreated(selection)
      
      // Check if we're in a modal context (selections will be saved when Accept is clicked)
      const isInModal = document.querySelector('.modal-backdrop') !== null
      
      if (!isInModal) {
        // Only auto-save if NOT in modal (regular viewer mode)
        try {
          const selectionData = {
            project_id: selection.project_id,
            document_id: selection.document_id,
            document_type: selection.document_type || 'pdf' as 'pdf' | 'image' | 'manual',
            x: selection.x,
            y: selection.y,
            width: selection.width,
            height: selection.height,
            page_number: selection.page_number || 1
          }
          
          console.log('💾 Auto-saving selection to backend:', selection.id)
          const savedSelection = await (this.apiService as any).createSelection(selectionData)

          // Update the selection with the real ID from backend
          this.ensureSelectionManager().updateSelection(selection.id, savedSelection)

          // Refresh local reference so downstream logic sees real ID immediately
          selection = { ...selection, ...savedSelection }

          // 🔄 UPDATE APP STATE: Replace the temporary selection with the saved one
          if (this.appState && this.appState.addSelection) {
            console.log('🔄 Updating app state with saved selection:', savedSelection.id)
            this.appState.addSelection(savedSelection)
          }

          console.log('✅ Selection auto-saved with ID:', savedSelection.id)

          // Use saved selection reference for downstream thumbnail/OCR pipeline
          selection = savedSelection

          // 🚀 IMMEDIATE OCR: Start OCR processing immediately when snippet is created
          console.log('🚀 Starting immediate OCR processing for selection:', savedSelection.id)
          this.startImmediateOCRProcessing(savedSelection)
        } catch (error) {
          console.error('Failed to auto-save selection:', error)
        }
      } else {
        console.log('📝 Selection created in modal - will save on Accept')
      }
      
      // Always generate thumbnail immediately
      this.generateThumbnailForSelection(selection)
    })
    
    this.ensureSelectionManager().setOnSelectionUpdated(() => {
      this.updateSelectionsGallery()
    })
    
    // Thumbnail generator callbacks
    this.ensureThumbnailGenerator().setOnThumbnailGenerated(async (selection, thumbnailData) => {
      logger.debug('Thumbnail generated for selection:', selection.id)
      
      // Update the selection with thumbnail data
      const selections = this.ensureSelectionManager().getSelections()
      const selectionToUpdate = selections.find(s => s.id === selection.id)
      if (selectionToUpdate) {
        selectionToUpdate.thumbnail_data = thumbnailData
        console.log('📸 Thumbnail updated for selection:', selection.id)
        
        // 🚀 IMMEDIATE UI UPDATE: Update app state immediately with thumbnail
        if (this.appState && this.appState.addSelection) {
          console.log('⚡ Updating app state with thumbnail for immediate UI feedback:', selection.id)
          this.appState.addSelection(selectionToUpdate)
        }
        
        // If this is a saved selection (has a real ID), update backend and trigger OCR
        if (selectionToUpdate.id && !selectionToUpdate.id.startsWith('selection_')) {
          try {
            console.log('🔄 Updating backend with thumbnail to trigger OCR:', selectionToUpdate.id)
            await (this.apiService as any).updateSelection(selectionToUpdate.id, {
              thumbnail_data: thumbnailData
            })
            console.log('✅ Backend updated with thumbnail')

            // Trigger OCR processing with the thumbnail (this will be handled by backend auto-OCR)
            console.log('🚀 Backend auto-OCR should process selection now that thumbnail is ready:', selectionToUpdate.id)
          } catch (error) {
            console.error('Failed to update backend with thumbnail:', error)
          }
        }
      }
      
      this.updateSelectionThumbnail(selection.id, thumbnailData)
      // Update gallery AFTER thumbnail is ready
      this.updateSelectionsGallery()
    })
    
    this.ensureThumbnailGenerator().setOnThumbnailError((selection, error) => {
      logger.error('Thumbnail generation failed for selection:', selection.id, error)
      this.updateSelectionThumbnail(selection.id, 'error')
      // Update gallery to show error state
      this.updateSelectionsGallery()
    })
  }
  
  async goToSelection(selectionId: string): Promise<void> {
    try {
      console.log('🎯 goToSelection called in refactored viewer:', selectionId)

      let targetSelection = this.ensureSelectionManager().getSelectionById(selectionId)
      console.log('🎯 Target selection from selection manager:', targetSelection)

      if (!targetSelection && this.appState && typeof this.appState.getSelections === 'function') {
        const globalSelections = this.appState.getSelections()
        targetSelection = globalSelections.find((selection: TextSelection) => selection.id === selectionId)
        console.log('🎯 Target selection from app state:', targetSelection)
      }

      if (!targetSelection) {
        console.warn(`❌ Selection not found for navigation: ${selectionId}`)
        return
      }

      console.log('🎯 Target selection details:', {
        id: targetSelection.id,
        page_number: targetSelection.page_number,
        document_id: targetSelection.document_id,
        x: targetSelection.x,
        y: targetSelection.y
      })

      const currentFile = this.ensureRenderer().getCurrentFile()
      const currentPage = this.ensureRenderer().getCurrentPage()
      console.log('🎯 Current state:', {
        currentFile: currentFile?.id,
        currentPage: currentPage,
        targetDocument: targetSelection.document_id,
        targetPage: targetSelection.page_number
      })

      if (!currentFile || currentFile.id !== targetSelection.document_id) {
        console.log('🎯 Target selection belongs to a different document. Attempting to load it.')
        const loaded = await this.loadDocumentForSelection(targetSelection)
        if (!loaded) {
          console.warn('❌ Unable to load document for selection navigation.')
          return
        }

        targetSelection = this.ensureSelectionManager().getSelectionById(selectionId)
        if (!targetSelection) {
          console.warn('❌ Selection not found after loading target document:', selectionId)
          return
        }
      }

      if (targetSelection.page_number && targetSelection.page_number !== this.ensureRenderer().getCurrentPage()) {
        console.log(`🎯 Navigating to page ${targetSelection.page_number} for selection ${selectionId}`)
        await this.ensureRenderer().goToPage(targetSelection.page_number)
        console.log(`✅ Navigated to page ${targetSelection.page_number}`)
      } else {
        console.log('🎯 Already on correct page, no navigation needed')
      }

      requestAnimationFrame(() => {
        console.log('🎯 Highlighting selection and scrolling into view')
        this.ensureSelectionManager().highlightSelectionById(selectionId)
        this.scrollSelectionIntoView(selectionId)
      })
    } catch (error) {
      console.error('❌ Failed to navigate to selection:', error)
    }
  }

  private scrollSelectionIntoView(selectionId: string): void {
    const overlay = this.container.querySelector('#selection-overlay')
    const container = this.container.querySelector('#pdf-canvas-container')

    if (!overlay || !container) return

    const selectionElement = overlay.querySelector(`[data-selection-id="${selectionId}"]`) as HTMLElement | null
    if (!selectionElement) {
      logger.debug('Selection element not found in overlay for scroll:', selectionId)
      return
    }

    const originalPointerEvents = selectionElement.style.pointerEvents
    selectionElement.style.pointerEvents = 'auto'

    selectionElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })

    setTimeout(() => {
      selectionElement.style.pointerEvents = originalPointerEvents
    }, 600)
  }

  private async loadDocumentForSelection(selection: TextSelection): Promise<boolean> {
    try {
      if (!selection.document_id) {
        logger.warn('Selection has no document_id; cannot load document for navigation.')
        return false
      }

      const documentInfo = await this.getDocumentMetadata(selection.document_id)
      if (!documentInfo) {
        logger.warn('Document metadata not found for selection navigation:', selection.document_id)
        return false
      }

      if (documentInfo.document_type && documentInfo.document_type !== 'pdf') {
        logger.warn('Navigation for non-PDF selections is not yet supported in refactored viewer.')
        return false
      }

      const projectId = documentInfo.project_id || selection.project_id || this.appState?.getCurrentProject?.()?.id || ''

      const pdfFile: PDFFile = {
        id: documentInfo.id,
        project_id: projectId,
        filename: documentInfo.filename || documentInfo.original_filename || 'document.pdf',
        pageCount: documentInfo.metadata?.page_count || documentInfo.page_count || selection.page_number || 1,
        dimensions: {
          width: documentInfo.metadata?.width || documentInfo.width || 0,
          height: documentInfo.metadata?.height || documentInfo.height || 0
        }
      }

      logger.debug('Loading PDF file for selection navigation:', pdfFile)
      await this.loadPDF(pdfFile, 0, false, true)
      return true
    } catch (error) {
      logger.error('Error loading document for selection navigation:', error)
      return false
    }
  }

  private async getDocumentMetadata(documentId: string): Promise<any | null> {
    if (this.documentCache.has(documentId)) {
      return this.documentCache.get(documentId)!
    }

    let documentInfo: any | null = null

    if (this.appState && typeof this.appState.getProjectDocuments === 'function') {
      const documents = this.appState.getProjectDocuments()
      const match = documents.find((doc: any) => doc.id === documentId)
      if (match) {
        documentInfo = match
      }
    }

    if (!documentInfo && this.appState && typeof this.appState.getDocumentById === 'function') {
      documentInfo = this.appState.getDocumentById(documentId)
    }

    if (!documentInfo && this.apiService && typeof (this.apiService as any).getDocumentInfo === 'function') {
      try {
        const response = await (this.apiService as any).getDocumentInfo(documentId)
        documentInfo = response?.document || response
      } catch (error) {
        logger.warn('Failed to fetch document metadata from API:', error)
      }
    }

    if (documentInfo) {
      this.documentCache.set(documentId, documentInfo)
    }

    return documentInfo || null
  }

  /**
   * Load a document (implements DocumentViewer interface)
   */
  async loadDocument(document: Document): Promise<void> {
    // Convert Document to PDFFile for compatibility
    const pdfFile: PDFFile = {
      id: document.id,
      project_id: document.project_id,
      filename: document.filename,
      pageCount: (document.metadata?.page_count || 0),
      dimensions: {
        width: (document.metadata?.width || 0),
        height: (document.metadata?.height || 0)
      }
    }
    
    await this.loadPDF(pdfFile)
  }
  
  /**
   * Load PDF with specific options (PDF-specific method)
   * @param file - The PDF file to load
   * @param retryCount - Number of retry attempts
   * @param skipAutoSelections - Controls gallery behavior (selections are always loaded)
   * @param _forceReload - Force reload flag (unused)
   */
  async loadPDF(file: PDFFile, retryCount: number = 0, skipAutoSelections: boolean = false, _forceReload: boolean = false): Promise<void> {
    try {
      logger.pdf('Loading PDF in refactored viewer:', file.filename)
      
      // Note: skipAutoSelections is part of the interface but not used here
      // as we always load selections. It's used by other parts of the system.
      // Suppress TypeScript warning by referencing the parameter
      void skipAutoSelections
      
      // Set current file in all components
      this.currentFile = file
      this.ensureSelectionManager().setCurrentFile(file)
      this.ensureThumbnailGenerator().setCurrentFile(file)
      
      // Load PDF in renderer
      await this.ensureRenderer().loadPDF(file, retryCount)
      
      // Sync scale with selection manager after PDF is loaded
      this.updateScaleFactor()
      
      // Always load existing selections - skipAutoSelections only controls gallery behavior
      await this.loadExistingSelections(file.id)
      
      // CRITICAL FIX: Sync scale again after selections are loaded to catch any late scale changes
      // Use setTimeout to ensure any fit-to-width or other scaling logic has completed
      setTimeout(() => {
        console.log('🔄 Final scale sync after PDF and selections loaded')
        this.updateScaleFactor()
      }, 100)
      
      logger.pdf('PDF loaded successfully in refactored viewer')
      
    } catch (error) {
      logger.error('Failed to load PDF in refactored viewer:', error)
      throw error
    }
  }
  
  /**
   * Create a new selection from coordinates
   */
  createSelection(coords: SelectionCoords): TextSelection {
    return this.ensureSelectionManager().createSelection(coords)
  }
  
  /**
   * Generate thumbnail for a selection
   */
  async getThumbnail(selection: TextSelection): Promise<string> {
    const scale = this.ensureRenderer().getScale()
    return this.ensureThumbnailGenerator().generateThumbnail(selection, scale)
  }
  
  /**
   * Get all current selections
   */
  getSelections(): TextSelection[] {
    return this.ensureSelectionManager().getSelections()
  }
  
  /**
   * Clear all state
   */
  clearState(): void {
    if (this.renderer) {
      this.renderer.clearState()
    }
    if (this.selectionManager) {
      this.selectionManager.clearState()
    }
    if (this.thumbnailGenerator) {
      this.thumbnailGenerator.clearState()
    }
    this.currentFile = null
    
    // Gallery cleanup handled by the gallery component itself
    
    logger.debug('PDFDocumentViewer state cleared')
  }
  
  /**
   * Destroy the viewer and clean up resources
   */
  destroy(): void {
    this.clearState()
    
    // Remove global reference
    delete (window as any).pdfViewerRefactored
    
    logger.debug('PDFDocumentViewer destroyed')
  }
  
  /**
   * Notify modal about new selection for count updates
   */
  private notifySelectionCreated(selection: TextSelection): void {
    try {
      // Dispatch custom event to notify modal about new selection
      const event = new CustomEvent('pdf-selection-created', {
        detail: { selection, viewer: this }
      })
      window.dispatchEvent(event)
      
      // Also update the gallery if it exists
      const galleryContainer = document.querySelector('#modal-selections-gallery')
      if (galleryContainer && (galleryContainer as any).selectionsGallery) {
        const gallery = (galleryContainer as any).selectionsGallery
        if (gallery.updateSelections) {
          gallery.updateSelections(this.getSelections())
        }
      }
      
      logger.debug('Notified modal about new PDF selection:', selection.id)
    } catch (error) {
      logger.error('Failed to notify modal about selection:', error)
    }
  }

  /**
   * Initialize selections gallery
   */
  initializeSelectionsGallery(): void {
    // Try multiple possible container IDs
    const possibleIds = [
      '#selections-gallery-container-main',
      '#modal-selections-gallery',
      '#selections-gallery'
    ]
    
    let galleryContainer: Element | null = null
    for (const id of possibleIds) {
      galleryContainer = document.querySelector(id)
      if (galleryContainer) {
        logger.debug(`Found gallery container with ID: ${id}`)
        break
      }
    }
    
    if (galleryContainer && !this.selectionsGallery) {
      this.selectionsGallery = new SelectionsGallery(galleryContainer as HTMLElement, {
        onSelectionReorder: (selections) => {
          logger.debug('Selection reorder callback')
          // Update app state if available
          if (this.appState && typeof this.appState.setSelections === 'function') {
            this.appState.setSelections(selections)
          }
        },
        onSelectionRemove: (id) => {
          logger.debug('Selection remove callback:', id)
          this.removeSelection(id)
        },
        onSelectionNavigate: (id) => {
          logger.debug('Selection navigate callback:', id)
          this.goToSelection(id)
        },
        onRetryThumbnail: (id) => {
          logger.debug('Retry thumbnail callback:', id)
          this.retryThumbnailGeneration(id)
        },
        getPDFName: (pdfId) => {
          // Try to get PDF name from app state
          if (this.appState && typeof this.appState.getProjectDocuments === 'function') {
            const documents = this.appState.getProjectDocuments()
            const doc = documents.find((d: any) => d.id === pdfId)
            return doc?.original_filename || doc?.filename || 'Unknown PDF'
          }
          // Fallback to current file if it matches
          if (this.currentFile && this.currentFile.id === pdfId) {
            return this.currentFile.filename || 'Unknown PDF'
          }
          return 'Unknown PDF'
        }
      })
      this.updateSelectionsGallery()
      logger.debug('Selections gallery initialized successfully')
    } else if (!galleryContainer) {
      logger.warn('Gallery container not found with any of the expected IDs:', possibleIds)
    } else {
      logger.debug('Selections gallery already initialized')
    }
  }
  
  /**
   * Remove a selection
   */
  public async removeSelection(selectionId: string): Promise<void> {
    try {
      logger.debug('Removing selection:', selectionId)
      
      // Remove from selection manager
      this.ensureSelectionManager().removeSelection(selectionId)
      
      // Remove from app state if available
      if (this.appState && typeof this.appState.removeSelection === 'function') {
        this.appState.removeSelection(selectionId)
      }
      
      // Update gallery
      this.updateSelectionsGallery()
      
      logger.debug('Selection removed successfully:', selectionId)
    } catch (error) {
      logger.error('Failed to remove selection:', error)
    }
  }

  /**
   * Retry thumbnail generation for a selection
   */
  public async retryThumbnailGeneration(selectionId: string): Promise<void> {
    try {
      logger.debug('Retrying thumbnail generation for selection:', selectionId)
      
      const selection = this.ensureSelectionManager().getSelectionById(selectionId)
      if (selection) {
        // Mark as loading
        selection.thumbnail_data = 'loading'
        this.updateSelectionsGallery()
        
        // Generate new thumbnail
        await this.generateThumbnailForSelection(selection)
      }
      
      logger.debug('Thumbnail generation retry completed for selection:', selectionId)
    } catch (error) {
      logger.error('Failed to retry thumbnail generation:', error)
    }
  }

  /**
   * Load existing selections for a PDF file
   */
  private async loadExistingSelections(fileId: string): Promise<void> {
    try {
      logger.pdf('Loading existing selections for file:', fileId)
      
      // Get the current project ID from the file
      const currentFile = this.currentFile
      console.log('🔍 DEBUG: Current file for loading selections:', currentFile)
      
      if (!currentFile || !currentFile.project_id) {
        console.log('❌ DEBUG: No current file or project ID available for loading selections')
        logger.debug('No current file or project ID available for loading selections')
        return
      }
      
      console.log('🔍 DEBUG: About to call API with project ID:', currentFile.project_id)
      
      // Use the correct API method: listProjectSelections
      const response = await this.apiService.listProjectSelections(currentFile.project_id)
      console.log('🔍 DEBUG: API response:', response)
      
      if (response && response.selections && Array.isArray(response.selections)) {
        // Filter selections for the current document
        const documentSelections = response.selections.filter((selection: any) => 
          selection.document_id === fileId
        )
        
        console.log('🔍 DEBUG: Filtered selections for document:', documentSelections)
        
        this.ensureSelectionManager().loadSelections(documentSelections)
        logger.pdf(`Loaded ${documentSelections.length} existing selections for document ${fileId}`)
      } else {
        console.log('❌ DEBUG: Invalid response structure:', response)
      }
    } catch (error) {
      console.log('❌ DEBUG: Error loading selections:', error)
      logger.debug('No existing selections found or error loading:', error)
      // This is not a critical error, continue without selections
    }
  }
  
  /**
   * Generate thumbnail for a newly created selection
   */
  private async generateThumbnailForSelection(selection: TextSelection): Promise<void> {
    try {
      logger.debug('Starting thumbnail generation for selection:', selection.id)
      
      // Selection coordinates are now already in PDF coordinate space (no scale factor needed)
      logger.debug('Selection coordinates (already in PDF space):', {
        x: selection.x,
        y: selection.y,
        width: selection.width,
        height: selection.height
      })
      
      // Generate fast thumbnail first for immediate feedback
      try {
        const fastThumbnail = await this.ensureThumbnailGenerator().generateFastThumbnail(selection, this.ensureRenderer().getScale())
        this.updateSelectionThumbnail(selection.id, fastThumbnail)
        logger.debug('Fast thumbnail generated for selection:', selection.id)
      } catch (fastError) {
        logger.warn('Fast thumbnail generation failed, continuing with high-quality:', fastError)
      }
      
      // Then generate high-quality thumbnail with timeout
      try {
        const highQualityThumbnail = await Promise.race([
          this.ensureThumbnailGenerator().generateThumbnail(selection, this.ensureRenderer().getScale()),
          new Promise<string>((_, reject) => 
            setTimeout(() => reject(new Error('High-quality thumbnail timeout')), 15000)
          )
        ])
        this.updateSelectionThumbnail(selection.id, highQualityThumbnail)
        logger.debug('High-quality thumbnail generated for selection:', selection.id)
      } catch (qualityError) {
        logger.warn('High-quality thumbnail generation failed:', qualityError)
        // Keep the fast thumbnail if high-quality fails
      }
      
    } catch (error) {
      logger.error('Failed to generate thumbnail for selection:', selection.id, error)
      this.updateSelectionThumbnail(selection.id, 'error')
    }
  }
  
  /**
   * Update thumbnail data for a selection
   */
  private updateSelectionThumbnail(selectionId: string, thumbnailData: string): void {
    this.ensureSelectionManager().updateSelection(selectionId, { thumbnail_data: thumbnailData })
  }
  
  /**
   * Update selections gallery
   */
  private updateSelectionsGallery(): void {
    if (this.selectionsGallery) {
      const selections = this.ensureSelectionManager().getSelections()
      logger.debug(`Updating selections gallery with ${selections.length} selections`)
      this.selectionsGallery.updateSelections(selections)
    } else {
      logger.debug('Selections gallery not initialized, skipping update')
    }
  }
  
  /**
   * Update scale factor in selection manager (called when PDF renderer scale changes, optimized)
   */
  updateScaleFactor(): void {
    if (this.selectionManager) {
      const newScale = this.ensureRenderer().getScale()
      console.log('🔄 PDFDocumentViewer.updateScaleFactor called:', newScale)
      // setScale now has built-in optimization to skip if scale hasn't changed
      this.selectionManager.setScale(newScale)
      logger.debug('Updated selection manager scale factor:', newScale)
    } else {
      console.log('🔄 PDFDocumentViewer.updateScaleFactor called but selectionManager is null')
    }
  }

  /**
   * Start immediate OCR processing for a selection as soon as it's created
   */
  private async startImmediateOCRProcessing(selection: any): Promise<void> {
    try {
      console.log('🚀 IMMEDIATE OCR: Starting immediate OCR for selection:', selection.id)
      
      // Get the page image for OCR processing immediately
      const pageImage = await this.getPageImageForSelection(selection)
      
      if (pageImage) {
        console.log('📸 Got page image for immediate OCR, size:', pageImage.length)
        
        // Process OCR immediately using the fast API
        const ocrResult = await Promise.race([
          (this.apiService as any).processFastOCR(pageImage, 'eng'),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Immediate OCR timeout')), 8000) // 8 second timeout for immediate OCR
          )
        ]) as any
        
        if (ocrResult && ocrResult.text && ocrResult.text.trim()) {
          console.log('✅ IMMEDIATE OCR completed:', ocrResult.text.substring(0, 50) + '...')
          
          // Update the selection with extracted text immediately
          await (this.apiService as any).updateSelection(selection.id, {
            extracted_text: ocrResult.text
          })
          
          // Update app state immediately
          if (this.appState && this.appState.addSelection) {
            const updatedSelection = { ...selection, extracted_text: ocrResult.text }
            this.appState.addSelection(updatedSelection)
            console.log('🔄 App state updated with immediate OCR result')
          }
          
          // Trigger UI refresh
          this.updateSelectionsGallery()
          console.log('🚀 IMMEDIATE OCR: UI updated with extracted text')
        } else {
          console.log('⚠️ IMMEDIATE OCR: No text extracted, will wait for thumbnail-based OCR')
        }
      } else {
        console.log('⚠️ IMMEDIATE OCR: No page image available, will wait for thumbnail-based OCR')
      }
    } catch (error) {
      console.error('❌ IMMEDIATE OCR failed:', error)
      // Don't throw - let the normal OCR pipeline handle it
    }
  }

  /**
   * Get page image for OCR processing
   */
  private async getPageImageForSelection(selection: any): Promise<string | null> {
    try {
      if (!this.pdfDocument) return null
      
      const page = await this.pdfDocument.getPage(selection.page_number || 1)
      const viewport = page.getViewport({ scale: 2.0 }) // Use 2x scale for OCR
      
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (!context) return null
      
      canvas.width = viewport.width
      canvas.height = viewport.height
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise
      
      // Convert to base64
      return canvas.toDataURL('image/png')
    } catch (error) {
      console.error('Failed to get page image for OCR:', error)
      return null
    }
  }

  
  /**
   * Render the PDF viewer UI
   */
  private render(): void {
    this.container.innerHTML = `
      <div class="space-y-4">
        <!-- PDF Controls -->
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <button 
              id="prev-page" 
              class="btn btn-outline"
              disabled
            >
              ← Previous
            </button>
            <div class="flex items-center space-x-2">
              <input 
                id="page-input" 
                type="number" 
                min="1" 
                max="1" 
                value="1"
                class="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-center"
                disabled
              />
              <span id="page-info" class="text-sm text-gray-600">
                of 0
              </span>
            </div>
            <button 
              id="next-page" 
              class="btn btn-outline"
              disabled
            >
              Next →
            </button>
          </div>
          
          <div class="flex items-center space-x-2">
            <button id="zoom-out" class="btn btn-outline text-sm">−</button>
            <span id="zoom-level" class="text-sm text-gray-600">120%</span>
            <button id="zoom-in" class="btn btn-outline text-sm">+</button>
            <button id="fit-width" class="btn btn-outline text-sm">Fit Width</button>
          </div>
        </div>
        
        <!-- PDF Canvas Container -->
        <div 
          id="pdf-canvas-container" 
          class="relative border border-gray-300 rounded-lg overflow-auto bg-gray-100"
          style="min-height: 450px; max-height: 65vh;"
        >
          <div id="pdf-loading" class="flex items-center justify-center absolute inset-0">
            <div class="text-center">
              <div class="spinner mb-2"></div>
              <div class="text-gray-600">Loading PDF...</div>
            </div>
          </div>
          
          <div id="pdf-content" class="relative hidden">
            <canvas id="pdf-canvas" class="mx-auto block"></canvas>
            <!-- Selection overlay -->
            <div id="selection-overlay" class="absolute inset-0 pointer-events-none"></div>
          </div>
        </div>
        
        <!-- Selection Tools -->
        <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div class="text-sm text-gray-600">
            <span id="selection-count">0</span> selections
          </div>
          <div class="flex space-x-2">
            <button id="clear-selections" class="btn btn-sm btn-outline">
              Clear All
            </button>
            <button id="generate-thumbnails" class="btn btn-sm btn-primary">
              Generate Thumbnails
            </button>
          </div>
        </div>
      </div>
    `
  }
  
  /**
   * Setup event listeners for UI controls
   */
  private setupEventListeners(): void {
    // Clear selections button
    const clearBtn = this.container.querySelector('#clear-selections') as HTMLButtonElement
    if (clearBtn) {
      clearBtn.onclick = () => {
        this.ensureSelectionManager().clearSelections()
        this.updateSelectionsGallery()
      }
    }
    
    // Generate thumbnails button
    const generateBtn = this.container.querySelector('#generate-thumbnails') as HTMLButtonElement
    if (generateBtn) {
      generateBtn.onclick = () => {
        const selections = this.ensureSelectionManager().getSelections()
        selections.forEach(selection => {
          this.generateThumbnailForSelection(selection)
        })
      }
    }
    
    // Update selection count display
    this.ensureSelectionManager().setOnSelectionUpdated((selections) => {
      const countEl = this.container.querySelector('#selection-count') as HTMLElement
      if (countEl) {
        countEl.textContent = selections.length.toString()
      }
    })
  }
  
  // Delegate methods to focused components for backward compatibility
  
  /**
   * Get current page number
   */
  getCurrentPage(): number {
    return this.ensureRenderer().getCurrentPage()
  }
  
  /**
   * Get total number of pages
   */
  getTotalPages(): number {
    return this.ensureRenderer().getTotalPages()
  }
  
  /**
   * Get current scale
   */
  getScale(): number {
    return this.ensureRenderer().getScale()
  }
  
  /**
   * Get current PDF file
   */
  getCurrentFile(): PDFFile | null {
    return this.currentFile
  }
  
  /**
   * Check if currently loading
   */
  getIsLoading(): boolean {
    return this.ensureRenderer().getIsLoading()
  }
  
  /**
   * Check if currently rendering
   */
  getIsRendering(): boolean {
    return this.ensureRenderer().getIsRendering()
  }
  
  /**
   * Navigate to next page
   */
  async nextPage(): Promise<void> {
    return this.ensureRenderer().nextPage()
  }
  
  /**
   * Navigate to previous page
   */
  async prevPage(): Promise<void> {
    return this.ensureRenderer().prevPage()
  }
  
  /**
   * Navigate to specific page
   */
  async goToPage(pageNumber: number): Promise<void> {
    return this.ensureRenderer().goToPage(pageNumber)
  }
  
  /**
   * Set zoom level
   */
  async setZoom(scale: number): Promise<void> {
    return this.ensureRenderer().setZoom(scale)
  }
  
  /**
   * Fit page to width
   */
  async fitToWidth(): Promise<void> {
    return this.ensureRenderer().fitToWidth()
  }

}
